import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { ComuneSelect } from "../../components/ComuneSelect";
import { toast } from "sonner";
import AgencyUsersModal from "./AgencyUsersModal";
import { useAuth } from "../../lib/auth";

const STATO_RETE = ["Fondatore", "Partner"];
const MAX_AGENTS = 5;
const empty = { name: "", comune: "Vicenza", provincia: "Vicenza", address: "", phone: "", email: "", stato_rete: "Partner", website: "", logo_url: "", agents: [] };

const resolveLogo = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

export default function AgenciesAdmin() {
  const nav = useNavigate();
  const { impersonateAgency } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [managingUsers, setManagingUsers] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const doImpersonate = async (a) => {
    const r = await impersonateAgency(a.id);
    if (!r.ok) { toast.error(r.error || "Impossibile avviare la sessione"); return; }
    toast.success(`Ora stai visualizzando come ${a.name}`);
    // replace: la history admin viene sostituita dall'Area Agenzie, così la freccia
    // browser "indietro" non riporta a viste admin vuote (l'utente è ora agency-scoped).
    nav("/area-agenzie", { replace: true });
  };

  const load = () => api.get(`/agencies${includeArchived ? "?include_archived=true" : ""}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [includeArchived]);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/agencies/${editing.id}`, editing);
      else await api.post("/agencies", editing);
      setEditing(null); load(); toast.success("Salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (a) => {
    if (!window.confirm(`Eliminare definitivamente "${a.name}"?\n\nQuesta operazione NON è reversibile e verrà bloccata se ci sono immobili, lead o utenti legati all'agenzia.\n\nSe vuoi solo nasconderla dal sito senza perdere i dati, usa "Archivia".`)) return;
    try {
      await api.delete(`/agencies/${a.id}`);
      toast.success("Agenzia eliminata");
      load();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Errore";
      toast.error(msg, { duration: 8000 });
    }
  };

  const archive = async (a) => {
    if (!window.confirm(`Archiviare "${a.name}"?\n\nL'agenzia sparirà dal sito pubblico e dalle liste, ma tutti i dati (immobili, lead, richieste) restano intatti nel DB. I login Area Agenzie verranno disabilitati. Puoi ripristinarla in qualsiasi momento.`)) return;
    try { await api.post(`/agencies/${a.id}/archive`); toast.success("Agenzia archiviata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const unarchive = async (a) => {
    try { await api.post(`/agencies/${a.id}/unarchive`); toast.success("Agenzia ripristinata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const addAgent = () => {
    if ((editing.agents || []).length >= MAX_AGENTS) return;
    setEditing({ ...editing, agents: [...(editing.agents || []), { name: "", phone: "", email: "" }] });
  };
  const updAgent = (i, k, v) => { const a = [...editing.agents]; a[i] = { ...a[i], [k]: v }; setEditing({ ...editing, agents: a }); };
  const rmAgent = (i) => setEditing({ ...editing, agents: editing.agents.filter((_, idx) => idx !== i) });

  const uploadLogo = async (file) => {
    if (!editing.id) {
      toast.info("Salva prima l'agenzia, poi torna qui per caricare il logo");
      return;
    }
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/agencies/${editing.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing({ ...editing, logo_url: data.logo_url });
      toast.success("Logo caricato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore upload logo"); }
  };

  return (
    <div data-testid="agencies-admin">
      <div className="flex justify-between items-end mb-8">
        <div><div className="label-uppercase">Anagrafica</div><h1 className="font-serif text-4xl mt-2">Agenzie Partner</h1></div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs label-uppercase text-[#5C5657] cursor-pointer">
            <input
              type="checkbox"
              data-testid="toggle-archived"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Mostra archiviate
          </label>
          <button data-testid="new-agency-btn" onClick={() => setEditing({ ...empty })} className="btn-primary">Nuova agenzia</button>
        </div>
      </div>
      <table className="admin-table w-full bg-white border border-[#E6E4E0]">
        <thead><tr><th>Nome</th><th>Stato rete</th><th>Comune</th><th>N. agenti</th><th>Immobili</th><th>Sito</th><th></th></tr></thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} data-testid={`agency-row-${a.id}`} className={a.archived ? "opacity-60" : ""}>
              <td className="font-medium">
                {a.name}
                {a.archived && <span className="ml-2 text-[10px] label-uppercase text-[#8A8484] border border-[#E6E4E0] px-2 py-0.5">Archiviata</span>}
              </td>
              <td>
                {a.stato_rete ? (
                  <span className={`badge-stamp text-[10px] ${a.stato_rete === "Fondatore" ? "badge-pronto" : "badge-criticita"}`}>{a.stato_rete === "Fondatore" ? "Partner Fondatore" : a.stato_rete}</span>
                ) : "—"}
              </td>
              <td>{a.comune}</td>
              <td>{a.agents?.length || 0}</td>
              <td>
                <Link
                  to={`/admin/immobili?agency=${a.id}`}
                  data-testid={`agency-properties-link-${a.id}`}
                  className="text-xs label-uppercase text-[#7D2E3F] hover:underline"
                >
                  {a.properties_count ?? 0} — vedi
                </Link>
              </td>
              <td className="text-xs">{a.website || "—"}</td>
              <td className="text-right space-x-3 whitespace-nowrap">
                <button data-testid={`ag-view-as-${a.id}`} onClick={() => doImpersonate(a)}
                        title="Accedi in modalità QA all'Area Agenzie di questa agenzia (nessun impatto sulle credenziali)"
                        className="text-xs label-uppercase text-[#245A2E]">Accedi come</button>
                <button onClick={() => setEditing(a)} className="text-xs label-uppercase text-[#7D2E3F]">Modifica</button>
                <button data-testid={`ag-users-${a.id}`} onClick={() => setManagingUsers(a)} className="text-xs label-uppercase text-[#7D2E3F]">Agenti</button>
                {a.archived ? (
                  <button data-testid={`ag-unarchive-${a.id}`} onClick={() => unarchive(a)} className="text-xs label-uppercase text-[#3A3D42]">Ripristina</button>
                ) : (
                  <button data-testid={`ag-archive-${a.id}`} onClick={() => archive(a)} className="text-xs label-uppercase text-[#91590F]">Archivia</button>
                )}
                <button data-testid={`ag-delete-${a.id}`} onClick={() => del(a)} className="text-xs label-uppercase text-[#B3261E]">Elimina</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-[#5C5657]">Nessuna agenzia</td></tr>}
        </tbody>
      </table>

      {managingUsers && (
        <AgencyUsersModal agency={managingUsers} onClose={() => setManagingUsers(null)} />
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E6E4E0] max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="font-serif text-2xl mb-6">{editing.id ? "Modifica agenzia" : "Nuova agenzia"}</h2>
            <div className="space-y-4">
              <div><label className="label-uppercase">Nome *</label><input data-testid="ag-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="admin-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label-uppercase">Stato rete *</label>
                  <select data-testid="ag-stato" value={editing.stato_rete || "Partner"} onChange={(e) => setEditing({ ...editing, stato_rete: e.target.value })} className="admin-input">
                    <option value="Fondatore">Partner Fondatore</option>
                    <option value="Partner">Partner</option>
                  </select>
                </div>
                <div><label className="label-uppercase">Provincia</label><input value={editing.provincia} onChange={(e) => setEditing({ ...editing, provincia: e.target.value })} className="admin-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label-uppercase">Comune *</label>
                  <ComuneSelect
                    value={editing.comune}
                    onChange={(v) => setEditing({ ...editing, comune: v })}
                    provincia={editing.provincia || null}
                    className="admin-input"
                    testid="ag-comune"
                  />
                </div>
                <div><label className="label-uppercase">Indirizzo</label><input placeholder="Via / Viale / n° civico" value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} className="admin-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label-uppercase">Telefono (rete fissa)</label><input placeholder="0444 000000" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="admin-input" /></div>
                <div>
                  <label className="label-uppercase">Email agenzia *</label>
                  <input type="email" data-testid="ag-email" placeholder="info@…" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="admin-input" />
                  <div className="text-[11px] text-[#8A8484] mt-1 leading-snug">
                    Unica destinataria dei lead/richieste sugli immobili di questa agenzia.
                    Le email degli agenti qui sotto restano solo per il login all&apos;Area Agenzie.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label-uppercase">Sito web</label><input value={editing.website || ""} onChange={(e) => setEditing({ ...editing, website: e.target.value })} className="admin-input" /></div>
                <div><label className="label-uppercase">Logo URL</label><input placeholder="https://…" value={editing.logo_url || ""} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} className="admin-input" /></div>
              </div>
              {/* Logo uploader */}
              <div className="border border-[#E6E4E0] bg-[#FBFAF7] p-4 flex items-center gap-5">
                <div className="w-24 h-24 bg-white border border-[#E6E4E0] flex items-center justify-center overflow-hidden shrink-0">
                  {editing.logo_url
                    ? <img src={resolveLogo(editing.logo_url)} alt="logo" className="w-full h-full object-contain p-1" />
                    : <span className="text-[10px] text-[#8A8484] text-center px-1">Nessun logo</span>}
                </div>
                <div className="flex-1">
                  <div className="label-uppercase">Logo agenzia</div>
                  <div className="text-xs text-[#5C5657] mt-1 mb-2">PNG · JPG · WEBP · SVG · max 5MB · sfondo trasparente consigliato</div>
                  {editing.id ? (
                    <label className="inline-block text-xs label-uppercase border border-[#E6E4E0] bg-white px-3 py-2 cursor-pointer hover:border-[#7D2E3F]">
                      Carica da file
                      <input type="file" accept="image/*" data-testid="ag-logo-upload" className="hidden"
                             onChange={(e) => { if (e.target.files?.[0]) uploadLogo(e.target.files[0]); e.target.value=""; }} />
                    </label>
                  ) : (
                    <div className="text-xs text-[#91590F]">Salva prima l'agenzia per abilitare l'upload da file.</div>
                  )}
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="label-uppercase">Agenti <span className="text-[#8A8484] normal-case">(fino a {MAX_AGENTS} · nome + cellulare + email)</span></label>
                  {(editing.agents || []).length < MAX_AGENTS && (
                    <button onClick={addAgent} className="text-xs label-uppercase text-[#7D2E3F]">+ Agente</button>
                  )}
                </div>
                <div className="space-y-2">
                  {(editing.agents || []).map((ag, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-1 text-xs label-uppercase text-[#8A8484] text-center">{i + 1}</div>
                      <input placeholder="Nome agente" value={ag.name} onChange={(e) => updAgent(i, "name", e.target.value)} className="admin-input col-span-4" />
                      <input placeholder="Cellulare" value={ag.phone || ""} onChange={(e) => updAgent(i, "phone", e.target.value)} className="admin-input col-span-3" />
                      <input placeholder="Email" type="email" value={ag.email || ""} onChange={(e) => updAgent(i, "email", e.target.value)} className="admin-input col-span-3" />
                      <button onClick={() => rmAgent(i)} className="text-xs text-[#B3261E] col-span-1">×</button>
                    </div>
                  ))}
                  {(editing.agents || []).length === 0 && <div className="text-xs text-[#8A8484] py-2">Nessun agente ancora inserito</div>}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setEditing(null)} className="btn-secondary">Annulla</button>
              <button data-testid="ag-save" onClick={save} className="btn-primary">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
